import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  TextField,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, query, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db, useFirebaseAuthentication } from 'lib/firebase';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import SiteWrapper from '../../home-pages/SiteWrapper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';

const MomStoryDetail = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const user = useFirebaseAuthentication();
  
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    const fetchStory = async () => {
      try {
        const docRef = doc(db, 'weekly_questions', storyId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setStory({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError('Story not found');
        }
      } catch (err) {
        setError('Failed to load story');
      } finally {
        setLoading(false);
      }
    };

    fetchStory();
  }, [storyId]);

  // Real-time comments listener
  useEffect(() => {
    if (!storyId) return;

    const q = query(
      collection(db, 'weekly_questions', storyId, 'comments'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setComments(commentsData);
    });

    return () => unsubscribe();
  }, [storyId]);

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !user) return;

    setSubmittingComment(true);
    try {
      await addDoc(collection(db, 'weekly_questions', storyId, 'comments'), {
        text: newComment.trim(),
        userId: user.uid,
        userName: user.displayName || 'Family Member',
        userPhoto: user.photoURL || null,
        createdAt: serverTimestamp(),
      });
      setNewComment('');
    } catch (err) {
      alert('Failed to post comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !story) {
    return (
      <SiteWrapper>
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error || 'Story not found'}</Alert>
          <Button variant="outlined" onClick={() => navigate('/mom-stories')}>
            Back to Stories
          </Button>
        </Container>
      </SiteWrapper>
    );
  }

  return (
    <SiteWrapper>
      <Helmet>
        <title>{story.questionText} - Mom Stories</title>
      </Helmet>
      <Box sx={{ minHeight: '100vh', bgcolor: '#fff', py: 6 }}>
        <Container maxWidth="md">
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/mom-stories')}
            sx={{ mb: 3 }}
          >
            Back to Stories
          </Button>

          <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, mb: 4 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                color: '#2c3e50',
                mb: 3
              }}
            >
              {story.questionText}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, color: 'text.secondary' }}>
              <Typography variant="subtitle2">
                {story.publishDate ? format(story.publishDate.toDate(), 'MMMM d, yyyy') : ''}
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: 'Georgia, serif',
                fontSize: '1.2rem',
                lineHeight: 1.8,
                color: '#333',
                whiteSpace: 'pre-wrap'
              }}
            >
              {story.momAnswer}
            </Typography>
          </Paper>

          {/* Comments Section */}
          <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Family Comments ({comments.length})
            </Typography>

            <List sx={{ mb: 3 }}>
              {comments.map((comment) => (
                <ListItem key={comment.id} alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar alt={comment.userName} src={comment.userPhoto} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" component="span" fontWeight="bold">
                          {comment.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {comment.createdAt ? format(comment.createdAt.toDate(), 'MMM d, h:mm a') : ''}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        {comment.text}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {user ? (
              <Box display="flex" gap={2}>
                <Avatar src={user.photoURL} alt={user.displayName} sx={{ width: 40, height: 40 }} />
                <Box flexGrow={1}>
                  <TextField
                    fullWidth
                    placeholder="Write a comment..."
                    multiline
                    rows={2}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 1, bgcolor: 'white' }}
                  />
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      endIcon={submittingComment ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      onClick={handleSubmitComment}
                      disabled={!newComment.trim() || submittingComment}
                      size="small"
                      sx={{
                        background: '#0066ff', // Prime One Blue
                        '&:hover': {
                          background: '#0052cc',
                        }
                      }}
                    >
                      Post
                    </Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                Please <Button color="primary" onClick={() => navigate('/signin')}>sign in</Button> to leave a comment.
              </Alert>
            )}
          </Paper>
        </Container>
      </Box>
    </SiteWrapper>
  );
};

export default MomStoryDetail;
